import { IconButton, Stack, Typography } from '@mui/material';
import type { IEmployeeCoverage } from '@sharedInterfaces/IOpportunity';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import MvpOpportunityDialog from '@src/App/Components/_UnderConstruction/Opportunities/MvpOpportunityDialog';
import CardBox from '@src/App/Components/Cards/CardBox/CardBox';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import type Opportunity from '@src/Objects/Opportunity';
import type { AppState } from '@store/store';
import { UsersRound } from 'lucide-react';
import React from 'react';
import { useSelector } from 'react-redux';

import MvpEmployeeCoverage from './MvpEmployeeCoverage';

export interface MvpSuggestedEmployeesCardProps {
    title?: string;
    suggestedEmployees: IEmployeeCoverage[];
    inDialog?: boolean;
    opportunity?: Opportunity;
    customIcon?: React.ReactNode;
    emptyStateText?: string;
    rightButtons?: React.ReactNode;
}

/**
 * Card wrapper for MvpEmployeeCoverage components with custom icon and empty state support
 */
function MvpSuggestedEmployeesCard({
    title,
    suggestedEmployees,
    inDialog,
    opportunity,
    customIcon,
    emptyStateText,
    rightButtons,
}: MvpSuggestedEmployeesCardProps) {
    const { openDialog } = useDialog();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).opportunities;

    const cardTitle = title || langStrings.suggestedEmployee;

    const handleEdit = () => {
        if (opportunity) {
            openDialog(
                <MvpOpportunityDialog
                    open={true}
                    onClose={() => {}}
                    opportunity={opportunity}
                    initialTab="suggestions"
                />,
            );
        }
    };

    // Sort employees by overall match score
    const sortedEmployees = [...suggestedEmployees].sort((a, b) => b.score - a.score);

    return (
        <CardBox
            title={cardTitle}
            showIcon={true}
            icon={customIcon || <UsersRound size={20} />}
            rightButtons={
                rightButtons ||
                (opportunity ? (
                    <IconButton size="small" onClick={handleEdit}>
                        {getAppIcon(EIconCategory.EDIT)}
                    </IconButton>
                ) : undefined)
            }
        >
            <Stack spacing={2}>
                {sortedEmployees.length > 0 ? (
                    sortedEmployees.map((emp) => (
                        <MvpEmployeeCoverage key={emp.id} suggestedEmployee={emp} inDialog={inDialog} />
                    ))
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        {emptyStateText || 'Keine Mitarbeiter vorhanden'}
                    </Typography>
                )}
            </Stack>
        </CardBox>
    );
}

export default MvpSuggestedEmployeesCard;
